import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";
import SearchableCourseSelect from "../../common/SearchableCourseSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown, faUpload } from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../../../utils/exportTemplate";

// include CourseTitle in the initial form
const initialForm = {
  Courses: "",
  Dept: "",
  Number: "",
  Capacity: "",
  CourseTitle: "",
  Lab: "",
};

// Spreadsheet columns, in the same order as the list view. Download Data,
// Download Template and Upload Data all use this one list so the template
// always matches what the grid shows and what upload reads back in.
const EXCEL_FIELDS = [
  "Courses",
  "CourseTitle",
  "Dept",
  "Number",
  "Capacity",
  "Lab",
];

// Excel hands back numbers for cells like Number/Capacity; store text so the
// filters' .toLowerCase() calls and string comparisons keep working.
const cellToText = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const CcpAdminCourses = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    Courses: "",
    Dept: "",
    Number: "",
    Capacity: "",
    CourseTitle: "",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
    duplicates: [], // Track duplicates in Excel upload
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const COLLECTION_NAME = "CcpCourses";

  useEffect(() => {
    // Fetch data from Firestore on mount
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      // Sort by Courses alphabetically
      rows = rows.sort((a, b) =>
        (a.Courses || "").localeCompare(b.Courses || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter(
      (row) =>
        (!filter.Courses ||
          (row.Courses || "")
            .toLowerCase()
            .includes(filter.Courses.toLowerCase())) &&
        (!filter.Dept ||
          (row.Dept || "").toLowerCase().includes(filter.Dept.toLowerCase())) &&
        (!filter.Number ||
          (row.Number || "")
            .toLowerCase()
            .includes(filter.Number.toLowerCase())) &&
        (!filter.Capacity ||
          (row.Capacity || "").toString().includes(filter.Capacity)) &&
        (!filter.CourseTitle ||
          (row.CourseTitle || "")
            .toLowerCase()
            .includes(filter.CourseTitle.toLowerCase())),
    );
    filtered = filtered.sort((a, b) =>
      (a.Courses || "").localeCompare(b.Courses || ""),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.Number === params.row.Number);
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLabChange = (value) => {
    setForm({ ...form, Lab: value });
  };

  // A course can't be its own lab, so exclude the course currently being
  // edited from the Lab dropdown's options.
  const labOptions = data.filter(
    (course) => course.Courses !== (editIndex !== null ? form.Courses : null),
  );

  const handleSave = async () => {
    if (
      !form.Courses ||
      !form.Dept ||
      !form.Number ||
      !form.Capacity ||
      !form.CourseTitle
    ) {
      setSnackbar({
        open: true,
        message: "All fields are required.",
        severity: "error",
      });
      return;
    }
    // Duplicate check for add form
    if (
      editIndex === null &&
      data.some(
        (d) =>
          d.Courses.trim().toLowerCase() === form.Courses.trim().toLowerCase(),
      )
    ) {
      setSnackbar({
        open: true,
        message: `A course with the name '${form.Courses}' already exists.`,
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        // Update Firestore document
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Course updated",
          severity: "success",
        });
      } else {
        // Add new Firestore document
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Course added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDeleteCourse = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Course deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
      duplicates: [],
    });
    const reader = new FileReader();

    const processExcelRow = async (
      row,
      merged,
      counts,
      seenCourses,
      duplicates,
      hasLabColumn,
    ) => {
      // Normalize key and fields from Excel ("Course Title" accepted as an
      // alias for older templates)
      const normalized = {
        Courses: cellToText(row.Courses),
        Dept: cellToText(row.Dept),
        Number: cellToText(row.Number),
        Capacity: cellToText(row.Capacity),
        CourseTitle: cellToText(row.CourseTitle ?? row["Course Title"]),
      };
      // Only touch Lab when the sheet has a Lab column, so uploading an older
      // template without it can't wipe Labs set through the Add/Edit form —
      // but a blank Lab cell in a sheet that has the column does clear it.
      if (hasLabColumn) normalized.Lab = cellToText(row.Lab);
      // Skip rows without a Courses value
      if (!normalized.Courses) return;
      // Duplicate check: only a course repeated within this same upload is a
      // duplicate. One that already exists in Firestore is updated below.
      const normalizedKey = normalized.Courses.toLowerCase();
      if (seenCourses.has(normalizedKey)) {
        duplicates.push(normalized.Courses);
        return;
      }
      seenCourses.add(normalizedKey);
      // Use Courses as the unique key
      const idx = merged.findIndex(
        (d) => (d.Courses || "").trim().toLowerCase() === normalizedKey,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        // merge: true — fields not in the sheet (e.g. Lab on an older
        // template) are left as they are rather than wiped out.
        await setDoc(doc(db, COLLECTION_NAME, docId), normalized, {
          merge: true,
        });
        merged[idx] = { ...merged[idx], ...normalized };
        counts.updated += 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
          Lab: "",
          ...normalized,
        });
        merged.push({ Lab: "", ...normalized, id: docRef.id });
        counts.added += 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      // defval: "" keeps blank cells as "" instead of dropping the key, so a
      // cleared cell is seen as cleared.
      const jsonData = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || [];
      const hasLabColumn = headerRow.some((h) => String(h).trim() === "Lab");

      const merged = [...data];
      const counts = { added: 0, updated: 0 };
      const seenCourses = new Set();
      const duplicates = [];

      for (const row of jsonData) {
        await processExcelRow(
          row,
          merged,
          counts,
          seenCourses,
          duplicates,
          hasLabColumn,
        );
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
          duplicates,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false, duplicates }));
    };

    reader.readAsBinaryString(file);
    // Clear the input so picking the same file again still triggers onChange
    e.target.value = "";
  };

  const handleDeleteMultipleConfirmed = async () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    try {
      setDeleting(true);
      // Delete each selected document by id
      for (const id of rowSelectionModel) {
        await deleteDoc(doc(db, COLLECTION_NAME, String(id)));
      }
      // Remove deleted rows from local state
      setData((prev) =>
        prev.filter((row) => !rowSelectionModel.includes(row.id)),
      );
      setRowSelectionModel([]);
      setConfirmDeleteOpen(false);
      setSnackbar({
        open: true,
        message: "Selected courses deleted",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting selected courses", err);
      setSnackbar({
        open: true,
        message: "Error deleting selected courses",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  // Downloads the rows currently shown (respecting the filters) in the same
  // columns as the template, so the file can be edited and uploaded back.
  const handleDownloadData = () => {
    if (!filteredData.length) {
      setSnackbar({
        open: true,
        message: "No data to download",
        severity: "warning",
      });
      return;
    }
    const rows = filteredData.map((row) =>
      EXCEL_FIELDS.map((field) => cellToText(row[field])),
    );
    const worksheet = XLSX.utils.aoa_to_sheet([EXCEL_FIELDS, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CcpCourses");
    XLSX.writeFile(workbook, "ccp-courses.xlsx");
  };

  const handleDownloadTemplate = () => {
    downloadExcelTemplate(EXCEL_FIELDS, "ccp-courses-template");
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    { field: "Courses", headerName: "Courses", flex: 1, sortable: true },
    {
      field: "CourseTitle",
      headerName: "Course Title",
      flex: 1.5,
      sortable: true,
    },
    { field: "Dept", headerName: "Dept", flex: 1, sortable: true },
    { field: "Number", headerName: "Number", flex: 1, sortable: true },
    { field: "Capacity", headerName: "Capacity", flex: 1, sortable: true },
    { field: "Lab", headerName: "Lab", flex: 1, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleOpenEdit(params)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
      width: 90,
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        CCP Courses Management
      </Typography>
      <CcpCpBreadcrumbs />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        sx={{ mt: 3, mb: 2 }}
      >
        <TextField
          label="Filter Courses"
          name="Courses"
          value={filter.Courses}
          onChange={(e) => setFilter({ ...filter, Courses: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Dept"
          name="Dept"
          value={filter.Dept}
          onChange={(e) => setFilter({ ...filter, Dept: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Number"
          name="Number"
          value={filter.Number}
          onChange={(e) => setFilter({ ...filter, Number: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Capacity"
          name="Capacity"
          value={filter.Capacity}
          onChange={(e) => setFilter({ ...filter, Capacity: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Course Title"
          name="CourseTitle"
          value={filter.CourseTitle}
          onChange={(e) =>
            setFilter({ ...filter, CourseTitle: e.target.value })
          }
          size="small"
        />
      </Stack>
      {/* Actions sit on their own row under the filters so neither row is
          crowded; wraps on narrow screens */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <Button
          variant="outlined"
          startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
          onClick={handleDownloadData}
          sx={{ whiteSpace: "nowrap" }}
        >
          Download Data
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          onClick={handleDownloadTemplate}
          sx={{ color: "white", whiteSpace: "nowrap" }}
        >
          Download Template
        </Button>
        <Button
          component="label"
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faUpload} />}
          disabled={importStatus.loading}
          sx={{ whiteSpace: "nowrap" }}
        >
          Upload Data
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </Button>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
          >
            Delete Selected
          </Button>
        )}
      </Stack>
      {importStatus.open && (
        <Box sx={{ mb: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total rows read: {importStatus.total}
            {importStatus.duplicates && importStatus.duplicates.length > 0 && (
              <>
                <br />
                Duplicates skipped: {importStatus.duplicates.join(", ")}
              </>
            )}
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
          display: "flex",
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={filteredData.length || 10}
          rowsPerPageOptions={[filteredData.length || 10]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseModal}>
          {editIndex !== null ? "Edit Course" : "Add Course"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Courses"
              name="Courses"
              value={form.Courses}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Dept"
              name="Dept"
              value={form.Dept}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Number"
              name="Number"
              value={form.Number}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Capacity"
              name="Capacity"
              value={form.Capacity}
              onChange={handleFormChange}
              required
              fullWidth
              type="number"
            />
            <TextField
              label="Course Title"
              name="CourseTitle"
              value={form.CourseTitle}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <SearchableCourseSelect
              courses={labOptions}
              value={form.Lab}
              onChange={handleLabChange}
              label="Lab"
              placeholder="Search and select a lab course..."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDeleteCourse : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        deleting={deleting}
        title="Delete Selected Courses"
        message={`Are you sure you want to delete ${rowSelectionModel.length} selected course${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default CcpAdminCourses;
